import { useCallback, useRef } from 'react';
import type { Density, Status, Story } from '@/types/board';
import { COLUMN_BY_ID } from '@/config/columns';
import { sumEstimates } from '@/store/selectors';
import { useBoard, useBoardStore } from '@/store/BoardContext';
import { useUi } from '@/store/UiContext';
import { useSortableColumn } from '@/hooks/useSortableColumn';
import { useContextMenu, type ContextMenuItem } from '@/components/ui/ContextMenu';
import { StoryCardCompact } from '@/components/cards/StoryCardCompact';
import { StoryCardNormal } from '@/components/cards/StoryCardNormal';
import './Column.css';

/**
 * One board column. Card density is resolved per column from settings, which
 * is why the same board can show tiles in Done and full cards in Active.
 */
export function Column({
  status,
  stories,
  density,
}: {
  status: Status;
  stories: Story[];
  density: Density;
}) {
  const def = COLUMN_BY_ID[status];
  const { moveStory, canEdit, updateSettings } = useBoardStore();
  const { settings } = useBoard();
  const ui = useUi();
  const showMenu = useContextMenu();
  const listRef = useRef<HTMLDivElement>(null);

  const onDrop = useCallback(
    (storyId: string, toStatus: Status, toIndex: number) => {
      moveStory(storyId, toStatus, toIndex);
    },
    [moveStory],
  );

  useSortableColumn(listRef, { status, onDrop });

  const points = sumEstimates(stories);
  const collapsed = ui.isColumnCollapsed(status);

  /** Open the editor with this column's status preselected. */
  const addHere = () => ui.openStoryEditor({ status });

  /**
   * Column menu. Density is here as well as in Settings because this is where
   * you notice a column is too dense to scan — walking to Settings to change one
   * column is friction the setting does not deserve.
   */
  const openColumnMenu = (event: React.MouseEvent) => {
    const ids = stories.map((s) => s.id);
    const items: ContextMenuItem[] = [
      { label: `New story in ${def.title}`, onSelect: addHere, disabled: !canEdit },
      {
        label: collapsed ? `Expand ${def.title}` : `Collapse ${def.title}`,
        separatorBefore: true,
        onSelect: () => ui.toggleColumnCollapsed(status),
      },
      {
        label: 'Expand all columns',
        onSelect: () => ui.setAllColumnsCollapsed(false),
      },
      {
        label: density === 'compact' ? 'Show full cards' : 'Show compact tiles',
        separatorBefore: true,
        onSelect: () =>
          updateSettings({
            board: {
              ...settings.board,
              density: {
                ...settings.board.density,
                [status]: density === 'compact' ? 'normal' : 'compact',
              },
            },
          }),
      },
      {
        label: 'Expand all task lists',
        separatorBefore: true,
        disabled: ids.length === 0,
        onSelect: () => ui.setAllExpanded(ids, true),
      },
      {
        label: 'Collapse all task lists',
        disabled: ids.length === 0,
        onSelect: () => ui.setAllExpanded([], false),
      },
    ];
    showMenu(event, items);
  };

  return (
    <section className="column" data-status={status} data-collapsed={collapsed}>
      <header className="column-head" onContextMenu={openColumnMenu}>
        {/*
          Collapsing the whole column matters most on a phone, where four stacked
          columns are four full-screen scrolls. It is a real button rather than
          only a context-menu entry because right-click does not exist on touch.
        */}
        <button
          className={`column-collapse${collapsed ? '' : ' open'}`}
          onClick={() => ui.toggleColumnCollapsed(status)}
          aria-expanded={!collapsed}
          aria-controls={`column-list-${status}`}
          title={collapsed ? `Expand ${def.title}` : `Collapse ${def.title}`}
        >
          <span className="caret">▾</span>
        </button>

        <span className="column-title">{def.title}</span>
        <span className="column-count">{stories.length}</span>
        {points > 0 ? <span className="column-points">{points} pts</span> : null}
        {canEdit && !collapsed ? (
          <button
            className="column-add"
            onClick={addHere}
            aria-label={`New story in ${def.title}`}
            title={`New story in ${def.title}`}
          >
            +
          </button>
        ) : null}

        {/*
          Touch has no right-click, so the density and collapse actions in the
          context menu would be unreachable on a phone without this. Hidden on
          pointer devices, where the context menu is the established gesture.
        */}
        <button
          className="column-more"
          onClick={openColumnMenu}
          aria-label={`${def.title} column options`}
          title="Column options"
        >
          ⋯
        </button>
      </header>

      <div
        className="column-list"
        id={`column-list-${status}`}
        ref={listRef}
        data-column-status={status}
        data-density={density}
        hidden={collapsed}
      >
        {stories.map((story) =>
          density === 'compact' ? (
            <StoryCardCompact key={story.id} story={story} />
          ) : (
            <StoryCardNormal key={story.id} story={story} />
          ),
        )}
      </div>

      {stories.length === 0 && !collapsed ? (
        canEdit ? (
          // Doubles as the drop-zone hint and a second, larger target — an empty
          // column is exactly where someone is most likely to want to add.
          <button className="empty empty-add" onClick={addHere}>
            + Add a story
          </button>
        ) : (
          <div className="empty">No stories</div>
        )
      ) : null}
    </section>
  );
}
